import type { JsonKind, JsonPath, JsonValue } from '../types';

export const inferType = (value: JsonValue): JsonKind => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  return 'string';
};

export const getValueAtPath = (root: JsonValue, path: JsonPath): JsonValue => {
  let current: JsonValue = root;

  for (const segment of path) {
    if (typeof segment === 'number') {
      if (!Array.isArray(current)) return null;
      current = current[segment] as JsonValue;
      continue;
    }

    if (current === null || Array.isArray(current) || typeof current !== 'object') return null;
    current = (current as Record<string, JsonValue>)[segment] as JsonValue;
  }

  return current;
};

/**
 * Structural-sharing update: only the objects/arrays along `path` are
 * shallow-cloned; every untouched sibling keeps its original reference.
 * This is dramatically cheaper than a full `JSON.parse(JSON.stringify(root))`
 * clone on every edit, which is what made editing large documents slow.
 */
export const setValueAtPath = (root: JsonValue, path: JsonPath, nextValue: JsonValue): JsonValue => {
  if (path.length === 0) return nextValue;

  const [head, ...rest] = path;

  if (typeof head === 'number') {
    if (!Array.isArray(root)) return root;
    const nextArray = root.slice();
    nextArray[head] = rest.length ? setValueAtPath(nextArray[head] ?? null, rest, nextValue) : nextValue;
    return nextArray;
  }

  if (root === null || Array.isArray(root) || typeof root !== 'object') return root;

  const nextObject: Record<string, JsonValue> = { ...(root as Record<string, JsonValue>) };
  nextObject[head] = rest.length ? setValueAtPath(nextObject[head] ?? null, rest, nextValue) : nextValue;
  return nextObject;
};

export const deleteAtPath = (root: JsonValue, path: JsonPath): JsonValue => {
  if (path.length === 0) return root;

  const parentPath = path.slice(0, -1);
  const leaf = path[path.length - 1];
  const parent = getValueAtPath(root, parentPath);

  if (typeof leaf === 'number') {
    if (!Array.isArray(parent)) return root;
    const nextArray = parent.slice();
    nextArray.splice(leaf, 1);
    return setValueAtPath(root, parentPath, nextArray);
  }

  if (parent === null || Array.isArray(parent) || typeof parent !== 'object') return root;

  const nextParent = { ...(parent as Record<string, JsonValue>) };
  delete nextParent[leaf];
  return setValueAtPath(root, parentPath, nextParent);
};

export const cloneJson = (value: JsonValue): JsonValue => {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(cloneJson);

  const result: Record<string, JsonValue> = {};
  for (const [key, child] of Object.entries(value)) result[key] = cloneJson(child);
  return result;
};

export const getUniqueObjectKey = (obj: Record<string, JsonValue>, desiredKey: string): string => {
  if (!Object.prototype.hasOwnProperty.call(obj, desiredKey)) return desiredKey;

  let index = 1;
  while (Object.prototype.hasOwnProperty.call(obj, `${desiredKey}_${index}`)) index += 1;
  return `${desiredKey}_${index}`;
};

/**
 * Insert a new array item cloned from an existing sibling's shape ("template
 * of previous entries") instead of a blank value, so the safe-edit policy's
 * shape-matching check passes by construction.
 */
export const addArrayItemWithTemplate = (
  array: JsonValue[],
  index: number,
  position: 'above' | 'below',
): JsonValue[] => {
  const insertIndex = position === 'above' ? index : index + 1;
  const templateSource = array.length > 0 ? array[Math.min(index, array.length - 1)] : '';
  const template = cloneJson(templateSource);
  const next = array.slice();
  next.splice(insertIndex, 0, template);
  return next;
};
